import { createI18n } from 'vue-i18n';

import { DEFAULT_LOCALE, SUPPORTED_CODES } from '@/config/locales';
import { getItemFromLocalStorage } from '@/helpers/localStorageServices';
// en is the default locale AND the fallbackLocale, so it must always be present — bundle it
// eagerly. Every other locale (es, zh) is fetched on demand by loadLocaleMessages (below).
import en from '@/locales/en.json';

// Initial locale: persisted choice → browser language (matched to a supported code) → default.
// The language store re-applies the authoritative value (incl. backend) on app start; this just
// avoids a flash of the wrong language before the store runs.
const resolveInitialLocale = () => {
  // Read with the same helper the store writes with (setItemToLocalStorage → JSON.stringify),
  // otherwise the value comes back quoted ('"es"') and never matches SUPPORTED_CODES.
  const saved = getItemFromLocalStorage('user-language');
  if (saved && SUPPORTED_CODES.includes(saved)) return saved;

  const browser = (navigator.language || '').split('-')[0];
  if (SUPPORTED_CODES.includes(browser)) return browser;

  return DEFAULT_LOCALE;
};

const i18n = createI18n({
  legacy: false,
  locale: resolveInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: { en },
  missing: (locale, key) => key, // Plain-English keys: fall back to the key itself.
  // The plain-English-key strategy makes "missing key → fall back to key/locale" the
  // normal path, so its warnings are pure dev noise. In composition mode (legacy: false)
  // the legacy silent* flags are ignored — missingWarn/fallbackWarn are the real switches.
  missingWarn: false,
  fallbackWarn: false,
  warnHtmlMessage: false,
});

const loaded = new Set(['en']);

// On-demand locale loading. en is bundled (default + fallbackLocale); es/zh are split into their
// own chunks and fetched the first time they are selected. Vite's dynamic-import-vars turns the
// template literal into a glob over src/locales/*.json. Idempotent — repeated calls are no-ops.
export const loadLocaleMessages = async (code) => {
  if (loaded.has(code)) return;
  const messages = await import(`../../locales/${code}.json`);
  i18n.global.setLocaleMessage(code, messages.default);
  loaded.add(code);
};

export default i18n;
